import { MultiStepFormState } from '@delmonicos/shared-design-system-react/src/contexts/multi-step-form-item';
import { SubmitHandlerWithDirtyFields } from '@delmonicos/shared-design-system-react/src/contexts/multi-step-form.provider';
import { getLockEmspUserDefaultValues } from '@delmonicos/shared-sdk/libs/cpms/src/libs/back-office-gateway-emsp-lib/src/inputs/emsp-users/lock-emsp-user.form-default-values';
import { UnlockEmspUserByIdModel } from '@delmonicos/shared-sdk/libs/cpms/src/libs/back-office-gateway-emsp-lib/src/inputs/emsp-users/unlock-emsp-user.model';
import React, { useState } from 'react';
import { SubmitErrorHandler } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { Logger } from '@app/core/logger/logger';
import { useEmspBoUnlockEmspUserMutation } from '@app/core/service/emsp-bo-emsp-user.service';

import {
  LockEmspBoEmspUserContextProvider,
  useEmspBoEmspUserContext,
} from '../emsp-bo-emsp-user.context';
import { EmspBoEmspUserLockDeleteFormController } from './emsp-bo-emsp-user-lock-delete-form/emsp-bo-emsp-user-lock-delete-form.controller';

interface EmspBoEmspUserLockDeleteControllerProps {
  onCancel: () => void;
  onSuccess: () => void;
}

export const EmspBoEmspUserLockDeleteController = ({
  onCancel,
  onSuccess,
}: EmspBoEmspUserLockDeleteControllerProps): React.JSX.Element => {
  // CONTEXT ///////////////////////////////////////////////////////////////////

  const { t } = useTranslation();
  const [unlockEmspUserMutation] = useEmspBoUnlockEmspUserMutation();
  const emspUser = useEmspBoEmspUserContext();
  const [multiStepFormState, setMultiStepFormState] = useState(
    MultiStepFormState.Default,
  );

  // HANDLERS //////////////////////////////////////////////////////////////////

  const handleError: SubmitErrorHandler<UnlockEmspUserByIdModel> = (errors) => {
    Logger.error(errors);
    toast.error(
      t('$.emsp-bo-emsp-user-lock-delete.error', {
        error: t('global.unknown'),
      }),
    );
  };

  const handleSubmit: SubmitHandlerWithDirtyFields<UnlockEmspUserByIdModel> = (
    fieldValues,
  ) => {
    setMultiStepFormState(MultiStepFormState.Loading);

    unlockEmspUserMutation(fieldValues)
      .unwrap()
      .then(() => {
        toast.success(t('$.emsp-bo-emsp-user-lock-delete.success'));
        onSuccess();
      })
      .catch(({ data: { message: errorMessage } }) => {
        toast.error(
          t('$.emsp-bo-emsp-user-lock-delete.error', {
            error: errorMessage,
          }),
        );
      })
      .finally(() => setMultiStepFormState(MultiStepFormState.Default));
  };

  // RENDER ////////////////////////////////////////////////////////////////////

  const data = getLockEmspUserDefaultValues(emspUser.id);

  return (
    <LockEmspBoEmspUserContextProvider data={data}>
      <EmspBoEmspUserLockDeleteFormController
        onError={handleError}
        onSubmit={handleSubmit}
        onCancel={onCancel}
        state={multiStepFormState}
      />
    </LockEmspBoEmspUserContextProvider>
  );
};
